import { createActions } from '@manager-base/app-base/config/data-access';
import { BASE_PATH_API } from '@manager-base/app-base/environment/paths/constants';

const actions = {
    getReportDefinition: {
        url: `${BASE_PATH_API}/reports/:reportCategory/:reportName/definition`,
        method: 'GET'
    },
    getReportConfiguration: {
        url: `${BASE_PATH_API}/reports/:category/:query/config`,
        method: 'GET'
    },
    getReportHtml: {
        url: `${BASE_PATH_API}/reports/:reportCategory/:reportName.html`,
        method: 'POST',
        transformResponse: function transformResponse(response, headers) {
            const resultsTotalHeader = headers.get('Results-Total');
            return resultsTotalHeader === null
                ? null
                : {
                    html: response ? response.trim() : '',
                    headers: {
                        ResultsTotal: parseInt(headers.get('Results-Total'), 10) || 0,
                        ResultsOffset: parseInt(headers.get('Results-Offset'), 10) || 0,
                        MaxNumberOfRowLimitExceeded: headers.get('X-buildium-number-rows-exceeded'),
                        LastRecordDate: headers.get('X-buildium-last-record-date'),
                        TotalNumberOfRows: parseInt(headers.get('X-buildium-total-rows'), 10) || 0,
                        MaxNumberOfRowLimit: parseInt(headers.get('X-buildium-rows-limit'), 10) || 0,
                    }
                };
        }
    },
    getFilterOptions: {
        url: `${BASE_PATH_API}/reportsCriteriaLookup/:populateFrom`,
        method: 'GET'
    },
    useInPageReportFilter: {
        url: `${BASE_PATH_API}/reports/:reportCategory/:reportName/useInPageReportFilter`,
        method: 'GET'
    },
    getUserReports: {
        url: `${BASE_PATH_API}/reports`,
        method: 'GET'
    },
    getReportCategories: {
        url: `${BASE_PATH_API}/reports/categories`,
        method: 'GET'
    },
    favoriteReport: {
        method: 'PUT',
        url: `${BASE_PATH_API}/users/self/reports/favorites/:id`,
    },
    unfavoriteReport: {
        method: 'DELETE',
        url: `${BASE_PATH_API}/users/self/reports/favorites/:id`,
    },
    emailReport: {
        method: 'POST',
        url: `${BASE_PATH_API}/reports/:reportCategory/:reportName.:format/email`,
    },
    searchEmailRecipients: {
        method: 'GET',
        cache: true,
        isArray: true,
        url: `${BASE_PATH_API}/reports/searchEmailRecipients`
    },
    getRecipientsByType: {
        method: 'GET',
        cache: true,
        isArray: true,
        url: `${BASE_PATH_API}/reports/:recipientType/recipients`
    },
    getRecipientTypeOptions: {
        method: 'GET',
        cache: true,
        isArray: true,
        url: `${BASE_PATH_API}/reports/:reportType/recipientTypeOptions`
    },
    getReportUserPrintSettings: {
        method: 'GET',
        cache: true,
        isArray: false,
        url: `${BASE_PATH_API}/reports/:reportId/printSettings`
    },
    saveReportUserPrintSettings: {
        method: 'PUT',
        url: `${BASE_PATH_API}/reports/:reportId/printSettings`
    },
    getAvailableReportsForBatchReports: {
        method: 'POST',
        url: `${BASE_PATH_API}/batch/reports`
    },
    getAvailableReportsForTemplate: {
        method: 'GET',
        url: `${BASE_PATH_API}/batch/template/available-reports`
    },
    getBatchReportTemplates: {
        method: 'GET',
        url: `${BASE_PATH_API}/batchReportTemplates`
    },
    getBatchReportTemplate: {
        method: 'GET',
        url: `${BASE_PATH_API}/batchReportTemplates/:templateId`
    },
    getTemplateFromBatchById: {
        method: 'GET',
        url: `${BASE_PATH_API}/batch/reports/:batchId/template`
    },
    getRecentBatchedReports: {
        method: 'GET',
        url: `${BASE_PATH_API}/batch/reports/recent`
    },
    getRecentBatchedReportsV2: {
        method: 'GET',
        url: `${BASE_PATH_API}/batch/reports/v2/recent`
    },
    createReportBatchTemplate: {
        method: 'POST',
        url: `${BASE_PATH_API}/batchReportTemplates`
    },
    updateReportBatchTemplate: {
        method: 'PUT',
        url: `${BASE_PATH_API}/batchReportTemplates/:id`
    },
    copyBatchReportTemplate: {
        method: 'POST',
        url: `${BASE_PATH_API}/batch/template/:templateId/copy`
    },
    deleteTemplate: {
        method: 'DELETE',
        url: `${BASE_PATH_API}/batch/template/:id`
    },
    getBatchReportTemplateRecipients: {
        method: 'POST',
        url: `${BASE_PATH_API}/batchReportTemplates/recipients`
    },
    getConfigurationsForReports: {
        method: 'POST',
        url: `${BASE_PATH_API}/batch/reports/configs`
    },
    generateBatchReportV2: {
        method: 'POST',
        url: `${BASE_PATH_API}/batch/reports/v2/batches`
    },
    enableNotifyUserForBatch: {
        method: 'PUT',
        url: `${BASE_PATH_API}/batch/reports/:batchId/notification`
    },
    getBudgets: {
        method: 'POST',
        url: `${BASE_PATH_API}/batch/reports/budgets`
    },
    getBankAccountsWithReconciliations: {
        method: 'POST',
        url: `${BASE_PATH_API}/batch/reports/bankAccountsWithReconciliations`
    },
    getEntitiesWithEftEnabledBankAccounts: {
        method: 'POST',
        url: `${BASE_PATH_API}/batch/reports/entitiesWithEftEnabledBankAccounts`
    },
    getEntitiesWithRetailCashEnabledBankAccounts: {
        method: 'POST',
        url: `${BASE_PATH_API}/batch/reports/entitiesWithRetailCashEnabledBankAccounts`
    },
    getBankAccountsWithOnlineCheckPrinting: {
        method: 'GET',
        url: `${BASE_PATH_API}/batch/reports/bankAccountsWithOnlineCheckPrintingEnabled`
    },
    getActivityLog: {
        method: 'GET',
        url: `${BASE_PATH_API}/activityLog`
    },
    getActivityLogHistory: {
        method: 'GET',
        url: `${BASE_PATH_API}/activityLog/:id`
    },
    downloadReport: {
        method: 'POST',
        url: `${BASE_PATH_API}/reports/:reportCategory/:reportName.:fileFormat/download:inlineQueryString`,
        responseType: 'blob',
        transformResponse: function transformResponse(response, headers) {
            if (!(response instanceof Blob)) {
                throw new Error('Invalid format to be downloaded');
            }

            //since the header can return more than the filename, we split it and return the last occurrence
            const contentDisposition = headers.get('content-disposition')
                ? headers.get('content-disposition').split('filename=')
                : [];
            return {
                data: response,
                filename: contentDisposition && contentDisposition.length > 1
                    ? contentDisposition[contentDisposition.length - 1]
                    : null,
                headers: {
                    ResultsTotal: parseInt(headers.get('Results-Total'), 10) || 0,
                    ResultsOffset: parseInt(headers.get('Results-Offset'), 10) || 0,
                    MaxNumberOfRowsLimitExceeded: headers.get('X-buildium-number-rows-exceeded'),
                    LastRecordDate: headers.get('X-buildium-last-record-date'),
                    TotalNumberOfRows: parseInt(headers.get('X-buildium-total-rows'), 10) || 0,
                    MaxNumberOfRowLimit: parseInt(headers.get('X-buildium-rows-limit'), 10) || 0,
                }
            };
        }
    },
    getFileEntities: {
        method: 'GET',
        url: `${BASE_PATH_API}/batch/reports/file/entities`,
        isArray: true
    },
    getFileCategories: {
        method: 'GET',
        url: `${BASE_PATH_API}/batch/reports/file/categories`,
        isArray: true
    },
    getBuildiumFiles: {
        method: 'GET',
        url: `${BASE_PATH_API}/batch/reports/file/buildiumFiles`
    },
    getBatchRecipients: {
        method: 'GET',
        url: `${BASE_PATH_API}/batch/reports/:batchId/recipients`,
        isArray: true
    },
    shareBatchReport: {
        method: 'POST',
        url: `${BASE_PATH_API}/batch/reports/:batchId/share`
    },
    templatesCountByRecurrence: {
        method: 'HEAD',
        url: `${BASE_PATH_API}/batchReportTemplates`,
        transformResponse: function transformResponse(response, headers) {
            return parseInt(headers.get('X-Total-Count'), 10);
        }
    },
    getBatchReportTemplateSettings: {
        method: 'GET',
        url: `${BASE_PATH_API}/batchReportTemplateSettings`
    },
    getReportById: {
        method: 'GET',
        cache: true,
        isArray: false,
        url: `${BASE_PATH_API}/reports/:reportId`
    }
};

export function ReportsApi() {
    return createActions(actions);
}
